import { useState, useEffect } from 'react';
import { getLeases, getApplications } from '../lib/rental-api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Home, 
  User, 
  Calendar, 
  DollarSign, 
  FileText, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  Plus
} from 'lucide-react';
import { Lease, TenantApplication } from '../types/lease';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

export function RentalManagement() {
  const [activeTab, setActiveTab] = useState('leases');
  const [selectedLease, setSelectedLease] = useState<Lease | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<TenantApplication | null>(null);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [applications, setApplications] = useState<TenantApplication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getLeases(), getApplications()])
      .then(([leasesData, appsData]) => {
        setLeases(leasesData);
        setApplications(appsData);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeLeases = leases.filter(l => l.status === 'active');
  const expiringLeases = leases.filter(l => l.status === 'expiring');
  const totalMonthlyRent = activeLeases.reduce((sum, l) => sum + l.monthlyRent, 0);
  const totalArrears = leases.reduce((sum, l) => sum + (l.arrearsAmount || 0), 0);

  const pendingApplications = applications.filter(a => a.status === 'pending');
  const openMaintenance: any[] = [];

  const getLeaseStatusBadge = (status: Lease['status']) => {
    const config = {
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      expiring: { label: 'Expiring Soon', className: 'bg-yellow-100 text-yellow-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
      terminated: { label: 'Terminated', className: 'bg-gray-100 text-gray-800' },
      pending: { label: 'Pending', className: 'bg-blue-100 text-blue-800' },
    };
    return <Badge className={config[status].className}>{config[status].label}</Badge>;
  };

  const getApplicationStatusBadge = (status: TenantApplication['status']) => {
    const config = {
      pending: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { label: 'Approved', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800', icon: AlertTriangle },
    };
    const Icon = config[status].icon;
    return (
      <Badge className={`${config[status].className} gap-1`}>
        <Icon className="h-3 w-3" />
        {config[status].label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2>Rental Management</h2>
          <p className="text-muted-foreground">
            Manage leases, tenants, and rental properties
          </p>
        </div>
        <Button data-testid="rental-management-new-lease-button">
          <Plus className="h-4 w-4 mr-2" />
          New Lease
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Leases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeLeases.length}</div>
            <p className="text-xs text-muted-foreground">{expiringLeases.length} expiring soon</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Monthly Rent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">R{totalMonthlyRent.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Recurring revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Arrears</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">R{totalArrears.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Overdue payments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingApplications.length + expiringLeases.length}</div>
            <p className="text-xs text-muted-foreground">
              {pendingApplications.length} applications, {expiringLeases.length} renewals
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leases">Active Leases</TabsTrigger>
          <TabsTrigger value="applications">
            Applications
            {pendingApplications.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                {pendingApplications.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="expiring">Expiring/Renewals</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>

        {/* Active Leases */}
        <TabsContent value="leases">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Lease Period</TableHead>
                    <TableHead>Next Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leases.map(lease => (
                    <TableRow key={lease.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lease.propertyTitle}</p>
                          <p className="text-sm text-muted-foreground">{lease.propertyAddress}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lease.tenantName}</p>
                          <p className="text-sm text-muted-foreground">{lease.tenantPhone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">R{lease.monthlyRent.toLocaleString()}</p>
                        {(lease.arrearsAmount ?? 0) > 0 && (
                          <p className="text-sm text-red-600">R{(lease.arrearsAmount ?? 0).toLocaleString()} overdue</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{new Date(lease.startDate).toLocaleDateString()}</p>
                          <p className="text-muted-foreground">to {new Date(lease.endDate).toLocaleDateString()}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{lease.nextPaymentDue ? new Date(lease.nextPaymentDue).toLocaleDateString() : '-'}</p>
                      </TableCell>
                      <TableCell>
                        {getLeaseStatusBadge(lease.status)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLease(lease)}
                          data-testid="rental-management-view-lease-button"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenant Applications */}
        <TabsContent value="applications">
          <div className="space-y-4">
            {applications.map(app => (
              <Card key={app.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h4 className="font-medium">{app.firstName} {app.lastName}</h4>
                        {getApplicationStatusBadge(app.status)}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Property</p>
                          <p className="font-medium">{app.propertyTitle}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Monthly Income</p>
                          <p className="font-medium">R{app.monthlyIncome.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Credit Score</p>
                          <p className="font-medium">{app.creditScore || 'Pending'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Applied</p>
                          <p className="font-medium">{new Date(app.applicationDate).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {app.agentNotes && (
                        <div className="mt-3 p-3 bg-muted rounded text-sm">
                          <p className="font-medium mb-1">Agent Notes:</p>
                          <p className="text-muted-foreground">{app.agentNotes}</p>
                        </div>
                      )}

                      {app.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded text-sm">
                          <p className="font-medium mb-1 text-red-900 dark:text-red-300">Rejection Reason:</p>
                          <p className="text-red-700 dark:text-red-400">{app.rejectionReason}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => setSelectedApplication(app)}
                        data-testid="rental-management-view-application-button"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                      {app.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toast.success('Application approved')}
                            data-testid="rental-management-approve-application-button"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toast.success('Application rejected')}
                            data-testid="rental-management-reject-application-button"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Expiring Leases */}
        <TabsContent value="expiring">
          <Card>
            <CardHeader>
              <CardTitle>Leases Requiring Attention</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {expiringLeases.map(lease => {
                  const daysUntilExpiry = Math.ceil(
                    (new Date(lease.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                  );

                  return (
                    <div key={lease.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{lease.propertyTitle}</p>
                        <p className="text-sm text-muted-foreground">
                          Tenant: {lease.tenantName} · Expires in {daysUntilExpiry} days
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" data-testid="rental-management-send-renewal-notice-button">
                          Send Renewal Notice
                        </Button>
                        <Button size="sm" data-testid="rental-management-renew-lease-button">
                          Renew Lease
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {expiringLeases.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No leases expiring in the next 90 days
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance */}
        <TabsContent value="maintenance">
          <div className="space-y-4">
            {openMaintenance.map(req => (
              <Card key={req.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{req.title}</h4>
                        <Badge 
                          variant={req.priority === 'urgent' ? 'destructive' : 'outline'}
                          className={
                            req.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            req.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }
                        >
                          {req.priority}
                        </Badge>
                      </div>

                      <p className="text-sm text-muted-foreground mb-2">{req.description}</p>

                      <div className="flex gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Property: <span className="font-medium">{req.propertyTitle}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Reported: <span className="font-medium">{new Date(req.reportedDate).toLocaleDateString()}</span>
                        </span>
                        {req.assignedTo && (
                          <span className="text-muted-foreground">
                            Assigned: <span className="font-medium">{req.assignedTo}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <Button size="sm" variant="outline" data-testid="rental-management-manage-maintenance-button">
                      Manage
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Lease Detail Dialog */}
      <Dialog open={!!selectedLease} onOpenChange={() => setSelectedLease(null)}>
        <DialogContent className="max-w-2xl">
          {selectedLease && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedLease.propertyTitle}</DialogTitle>
                <DialogDescription>{selectedLease.propertyAddress}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Tenant</p>
                    <p className="font-medium">{selectedLease.tenantName}</p>
                    <p className="text-sm">{selectedLease.tenantEmail}</p>
                    <p className="text-sm">{selectedLease.tenantPhone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Landlord</p>
                    <p className="font-medium">{selectedLease.landlordName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Rent</p>
                    <p className="font-semibold text-lg">R{selectedLease.monthlyRent.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Deposit</p>
                    <p className="font-semibold text-lg">R{selectedLease.deposit.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Escalation</p>
                    <p className="font-semibold text-lg">{selectedLease.escalationRate}%</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-2">Lease Period</p>
                  <p>{new Date(selectedLease.startDate).toLocaleDateString()} - {new Date(selectedLease.endDate).toLocaleDateString()}</p>
                </div>

                {(selectedLease.arrearsAmount ?? 0) > 0 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded">
                    <p className="font-medium text-red-900 dark:text-red-300">
                      Arrears: R{(selectedLease.arrearsAmount ?? 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                      Payment overdue - contact tenant immediately
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
